import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { mapValues } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/*
  Helpers for MaterialReactTable columns whose data holds an enumeration id (or any id that is shown as a looked-up
  title, such as a vendor or address id).

  RMT column filters, the global search and sorting all work off a cell's value (row.getValue), never off what its
  Cell renders. A column that stores an id and only maps it to a title in Cell therefore filters and sorts on the id.
  enumColumn makes the column's value the option's title instead, so typing the text the user sees matches. The id
  stays in row.original, which is what edit forms read from.

  Usage:
    { ...enumColumn('countryId', countries), header: 'Country', type: 'dropdown-v2', options: countries }

  Common enumerations have named helpers that find their own options, so a column does not depend on where the
  enumeration is kept:
    const { countryColumn } = useEnumColumns();
    { ...countryColumn('countryId'), header: 'Country', type: 'dropdown-v2', options: countries }

  The column renders its title without a Cell; add one only to wrap or style it, reading the title with
  cell.getValue() and the id with row.original.

  Rows cache their values and are only rebuilt when the data array changes, so a table that renders enumColumn
  columns must pass its data through useEnumColumnData. CrudTable and DashboardDataTable already do.
*/

const getOptionLabel = (option, label) => (typeof label === 'function' ? label(option) : option[label]);

// Ids are compared as text: GUIDs can come back from the API in a different case than the options hold them in, and
// numeric ids sometimes arrive as strings.
const idsMatch = (a, b) => a === b || (a != null && b != null && isIdEqual(String(a), String(b)));

/**
 * Looks up the display text for an id.
 * @param {Array} options - the options to look the id up in
 * @param {*} value - the id, or an array of ids (their titles are joined with ', ')
 * @param {object} [config]
 * @param {string} [config.valueKey='id'] - the option property holding the id
 * @param {string|function} [config.label='title'] - the option property holding the text, or option => text
 * @returns {string} the text, or '' when the value is empty or not among the options
 */
export const getEnumTitle = (options, value, { valueKey = 'id', label = 'title' } = {}) => {
  if (Array.isArray(value)) {
    return value
      .map(item => getEnumTitle(options, item, { valueKey, label }))
      .filter(Boolean)
      .join(', ');
  }
  if (value === null || value === undefined) return '';
  const option = (options ?? []).find(opt => idsMatch(opt?.[valueKey], value));
  return (option && getOptionLabel(option, label)) || '';
};

// Changes when options load or are swapped for another list, which is when cached row values go stale.
const getOptionsKey = (options, valueKey) =>
  options?.length ? `${options.length}:${options[0]?.[valueKey]}:${options[options.length - 1]?.[valueKey]}` : '0';

/**
 * Column props for an MRT column whose data holds an id that is displayed as an option's title. Spread them into the
 * column definition; the column's value (what it renders, filters, searches and sorts on) becomes the title.
 * @param {string} accessorKey - the row property holding the id
 * @param {Array} options - the options to look the id up in
 * @param {object} [config] - valueKey and label, as for getEnumTitle
 */
export const enumColumn = (accessorKey, options, config = {}) => ({
  accessorKey,
  accessorFn: row => getEnumTitle(options, row[accessorKey], config),
  enumOptionsKey: getOptionsKey(options, config.valueKey ?? 'id'),
});

const getColumnsOptionsKey = columns =>
  (columns ?? []).map(column => (column.columns ? getColumnsOptionsKey(column.columns) : column.enumOptionsKey ?? '')).join('|');

/**
 * Returns the table data to give MaterialReactTable: data itself, as a new array whenever the options of an
 * enumColumn column change, so rows rebuilt from it pick up titles for options that loaded after the rows did.
 */
export const useEnumColumnData = (data, columns) => {
  const optionsKey = getColumnsOptionsKey(columns);
  // optionsKey stands in for the columns, which callers usually rebuild on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => (Array.isArray(data) ? [...data] : data), [data, optionsKey]);
};

// The enumeration each named column helper looks its ids up in, by its key in state.enumerations.allEnums. Add an
// entry to give another enumeration a helper.
const ENUM_COLUMN_KEYS = {
  addressTypeColumn: 'addressTypes',
  countryColumn: 'countries',
  currencyColumn: 'currencies',
  fieldStatusColumn: 'fieldStatuses',
  fieldTypeColumn: 'fieldTypes',
  provinceColumn: 'provinces',
  schoolingLevelColumn: 'schoolingLevels',
};

/**
 * Builds the named column helpers ({ countryColumn, provinceColumn, ... }) over enumerations, the
 * state.enumerations.allEnums object. Each takes the accessorKey and returns enumColumn's props, looking the id up in
 * every value of its enumeration so a record holding a since-deactivated value still shows its title.
 *
 * For code that already has the enumerations or cannot call hooks, such as a column builder function; components
 * can use useEnumColumns instead.
 */
export const getEnumColumns = enumerations =>
  mapValues(ENUM_COLUMN_KEYS, key => accessorKey => enumColumn(accessorKey, enumerations?.[key]));

/**
 * The named column helpers over the enumerations in the store.
 */
export const useEnumColumns = () => {
  const enumerations = useSelector(state => state.enumerations.allEnums);
  return useMemo(() => getEnumColumns(enumerations), [enumerations]);
};
